import React, { useEffect, useState } from "react";
import { 
  trackStoreOpen, 
  trackStoreWebsiteVisit, 
  trackProductView, 
  trackGetDirections 
} from './utils/analytics';

// https://www.geodatasource.com/developers/javascript
function distance(position1,position2){
  var lat1 = parseFloat(position1.Lat);
  var lat2 = parseFloat(position2.lat);
  var lon1 = parseFloat(position1.Lng);
  var lon2 = parseFloat(position2.lng);

  var radlat1 = Math.PI * lat1/180;
  var radlat2 = Math.PI * lat2/180;
  var theta = lon1-lon2;
  var radtheta = Math.PI * theta/180;
  var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = dist * 180/Math.PI;
  dist = dist * 60 * 1.1515;
  return dist;
}

Array.prototype.sortOn = function(key){
    this.sort(function(a, b){
        if(a[key] < b[key]){
            return -1;
        }else if(a[key] > b[key]){
            return 1;
        }
        return 0;
    });
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Move getLocationProducts outside the component to prevent recalculation
function getLocationProducts(locationLitAlertsId, allProducts) {
  if (!locationLitAlertsId || !allProducts?.data) {
    return [];
  }
  
  return allProducts.data.filter(product => 
    product.retailerId === parseInt(locationLitAlertsId, 10)
  );
}

// Helper function to format price as dollar amount
function formatPrice(price) {
  if (typeof price !== 'number') return '';
  return price % 1 === 0 
    ? `$${price}` 
    : `$${price.toFixed(2)}`;
}

const Locations = (props) => {
  const [locations, setLocations] = useState([]);
  const [displayCount, setDisplayCount] = useState(10);
  const [products, setProducts] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [openModalId, setOpenModalId] = useState(null);
  const [modalData, setModalData] = useState(null);
  
  // New state for product filtering
  const [productNameFilter, setProductNameFilter] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [searchMode, setSearchMode] = useState('exclusive');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);

  let pickupOrDelivery = '';
  // if we are delivering, we filter by city since Delivery is based on which towns they deliver to
  if ('delivery' === props.pickupOrDelivery) {
    pickupOrDelivery = '{Delivery}=1';
  } else {
    pickupOrDelivery = '{Pickup}=1';
  }

  let rangeSearchString = '';
  let deliveryAreaSearchString = '';

  // filter by 1 degree Latitude (-0.5, 0.5) and Longitude (-0.5, 0.5) of the shopper's location
  if (props.shopperLocation && 'pickup' === props.pickupOrDelivery) {
    // console.log(props.shopperLocation);
    let lat1 = parseFloat(props.shopperLocation.lat, 10);
    lat1 = lat1 - 0.5;
    let lat2 = lat1 + 1.0;
    let lng1 = parseFloat(props.shopperLocation.lng, 10);
    lng1 = lng1 - 0.5;
    let lng2 = lng1 + 1.0;
    // console.log(lat1,lat2,lng1,lng2);
    rangeSearchString = ',{Lat}>' + lat1 + ',{Lat}<' + lat2 + ',{Lng}>' + lng1 + ',{Lng}<' + lng2;
  } else if ('delivery' === props.pickupOrDelivery) {
    if (props.shopperLocation?.city) {
      deliveryAreaSearchString = `,FIND("${props.shopperLocation.city}",ARRAYJOIN({DeliveryAreasLookup},","))`;
    }
  }

  let searchParams = new URLSearchParams({
    'filterByFormula': 'AND(' + pickupOrDelivery + rangeSearchString + deliveryAreaSearchString + ',{Visible}=1)'
  });

  // https://airtable.com/appNZ3Lf5arpmocRF/api/docs#curl/table:table%201:list
  // have to hard code this URLSearchParams doesn't format the array correctly
  let fields = "fields[]=Name&fields[]=DeliveryURL&fields[]=PickupURL&fields[]=City&fields[]=State&fields[]=Lat&fields[]=Lng&fields[]=DeliveryAreasLookup&fields[]=Image&fields[]=LitAlertsId";

  // Add new function to fetch products
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      console.log('Fetching products...');
      const response = await fetch(`${API_BASE_URL}/api/products`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Products API error:', response.status, errorText);
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      setProducts(data);
      setLoadingProducts(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoadingProducts(false);
    }
  };

  // Add new useEffect for products
  useEffect(() => {
    fetchProducts();
  }, []); // Empty dependency array means this runs once on component mount

  useEffect(() => {
    setLoadingLocations(true);

    const params = new URLSearchParams({
      fields: fields,
      filter: 'AND(' + pickupOrDelivery + rangeSearchString + deliveryAreaSearchString + ',{Visible}=1)'
    });

    console.log('Location search for:', {
      city: props.shopperLocation?.city,
      type: props.pickupOrDelivery,
      lat: props.shopperLocation?.lat,
      lng: props.shopperLocation?.lng
    });
    console.log('Search formula:', params.get('filter'));

    console.log('Fetching locations...');
    fetch(`${API_BASE_URL}/api/locations?${params}`)
      .then(async (res) => {
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Locations API error:', res.status, errorText);
          throw new Error(`API error: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log('Received locations:', data.records.length);
        if ('delivery' === props.pickupOrDelivery) {
          setLocations(data.records);
        } else {
          setLocations(sortByDistance(data.records, props.shopperLocation));
        }
        setLoadingLocations(false);
      })
      .catch((error) => {
        console.error('Error fetching locations:', error);
        setLoadingLocations(false);
      });
  }, [
    props.shopperLocation?.city,
    props.shopperLocation?.lat,
    props.shopperLocation?.lng,
    props.pickupOrDelivery,
    fields,
    pickupOrDelivery,
    rangeSearchString,
    deliveryAreaSearchString
  ]); // Add all dependencies

  function sortByDistance(records, shopperLocation) {
    // calculate distances based on location
    records.map((record) => (
      record.distance = distance(record.fields, shopperLocation)
    ));
    // sort
    records.sortOn('distance');
    return records;
  }

  function LocationImage(props) {
    if (props.recordImage && props.recordImage.length > 0) {
      return (
        <div className="store-image">
          <img src={props.recordImage[0]['thumbnails']['large']['url']} />
        </div>
      );
    } else {
      return (<div className="store-image"></div>)
    }
  }

  // Memoize the product mapping to prevent recalculation
  const locationProductsMap = React.useMemo(() => {
    const map = new Map();
    if (products?.data) {
      locations.forEach(record => {
        if (record.fields.LitAlertsId) {
          map.set(record.fields.LitAlertsId, getLocationProducts(record.fields.LitAlertsId, products));
        }
      });
    }
    return map;
  }, [products, locations]);

  // Update ProductModal component
  function ProductModal({ products, isOpen, onClose, location }) {
    React.useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === 'Escape') onClose();
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const handleGetDirections = (e) => {
      e.preventDefault();
      trackGetDirections(location);
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${location.fields.Lat},${location.fields.Lng}`,
        '_blank'
      );
    };

    const handleProductClick = (e, product) => {
      // Only if the click was not on an interactive element like a button
      if (!e.target.closest('button')) {
        trackProductView(product, location);
      }
    };

    const handleButtonClick = (e, product) => {
      e.stopPropagation();
      trackProductView(product, location);
    };

    if (!isOpen) return null;
    
    return (
      <div 
        className="modal-overlay" 
        onMouseDown={(e) => {
          if (e.target.classList.contains('modal-overlay')) {
            onClose();
          }
        }}
      >
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{location.fields.Name}</h3>
            <div className="modal-actions">
              <a 
                href={`https://www.google.com/maps/dir/?api=1&destination=${location.fields.Lat},${location.fields.Lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="directions-button"
                onClick={handleGetDirections}
              >
                Get Directions
              </a>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }} 
                className="close-button"
              >
                &times;
              </button>
            </div>
          </div>
          <div className="modal-body">
            <div className="store-address">
              {location.fields.City}, {location.fields.State}
            </div>
            <ul className="product-list">
              {products.map(product => {
                const config = product.configs[0];
                const stockStatus = config?.currentStock <= 5 
                  ? 'low' 
                  : config?.currentStock > 0 
                    ? 'in-stock' 
                    : 'out-of-stock';
                const hasValidUrl = !!product.recreationalURL && product.recreationalURL.startsWith('http');
                
                return (
                  <li key={product.id} className={stockStatus} onClick={(e) => handleProductClick(e, product)}>
                    <a href={product.recreationalURL} target="_blank" rel="noopener noreferrer">
                      <div className="product-header">
                        <span className="product-name">{product.name}</span>
                        <div className="price-info">
                          {config?.salePrice ? (
                            <>
                              <span className="original-price">{formatPrice(config.normalPrice)}</span>
                              <span className="sale-price">{formatPrice(config.salePrice)}</span>
                            </>
                          ) : (
                            <span className="normal-price">{formatPrice(config?.normalPrice)}</span>
                          )}
                        </div>
                      </div>
                      <div className="product-details">
                        <div className="left-info">
                          <div className="stock-count">
                            {config?.currentStock <= 0 ? (
                              <span className="out-of-stock">Out of Stock</span>
                            ) : config?.currentStock <= 5 ? (
                              <span className="low-stock">Only {config.currentStock} left!</span>
                            ) : (
                              <span className="in-stock">{config.currentStock > 20 ? '20+ units' : `${config.currentStock} units`} available</span>
                            )}
                          </div>
                        </div>
                        <div className="right-info">
                          {hasValidUrl && (
                            <button 
                              className={`view-item-button ${config?.currentStock <= 0 ? 'disabled' : ''}`}
                              disabled={config?.currentStock <= 0}
                              onClick={(e) => handleButtonClick(e, product)}
                            >
                              {config?.currentStock <= 0 ? 'Out of Stock' : 'View Details'}
                            </button>
                          )}
                        </div>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className="price-disclaimer">
              Prices shown are approximate and may not include taxes. Product availability and final pricing 
              may vary at time of purchase.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Update the openModal handler
  const handleOpenModal = (record, products) => {
    trackStoreOpen(record);
    setModalData({ location: record, products });
  };

  // Add new function to fetch filtered products
  const fetchFilteredProducts = async () => {
    if (!productNameFilter && !minPrice && !maxPrice) {
      setFilteredProducts(null);
      return;
    }
    
    setIsFiltering(true);
    
    try {
      let url = `${API_BASE_URL}/api/products/cheapest?limit=50`;
      
      if (productNameFilter) {
        url += `&name=${encodeURIComponent(productNameFilter)}&searchMode=${searchMode}`;
      }
      
      if (minPrice) {
        url += `&minPrice=${parseFloat(minPrice)}`;
      }
      
      if (maxPrice) {
        url += `&maxPrice=${parseFloat(maxPrice)}`;
      }
      
      console.log('Fetching filtered products:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`Found ${data.count} products matching filters`);
      setFilteredProducts(data);
    } catch (error) {
      console.error('Error fetching filtered products:', error);
    } finally {
      setIsFiltering(false);
    }
  };
  
  // Handle form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchFilteredProducts();
  };
  
  // Clear filters
  const clearFilters = () => {
    setProductNameFilter('');
    setMinPrice('');
    setMaxPrice('');
    setSearchMode('exclusive');
    setFilteredProducts(null);
  };

  // Update the LocationRow component to highlight matched products
  const LocationRow = React.memo(({ record, type, hasStock, products, matchedProductCount }) => {
    const url = type === 'delivery' ? record.fields.DeliveryURL : record.fields.PickupURL;
    
    const handleRowClick = () => {
      if (hasStock) {
        handleOpenModal(record, products);
      } else {
        trackStoreWebsiteVisit(record, type);
        window.open(url, '_blank');
      }
    };
    
    return (
      <div 
        className={`store-row ${hasStock ? 'has-stock' : ''} ${matchedProductCount ? 'has-matches' : ''}`}
        onClick={handleRowClick}
      >
        <div className="store-content">
          <LocationImage recordImage={record.fields['Image']} />
          <div className="store-info">
            <span className="store-name">{record.fields.Name}</span>
            {type === 'delivery' ? (
              <span>Delivery areas: {record.fields.DeliveryAreasLookup && record.fields.DeliveryAreasLookup.join(', ')}</span>
            ) : (
              <span>{record.fields.City}, {record.fields.State} - {record.distance && record.distance.toLocaleString(undefined, {maximumFractionDigits:2})} miles away</span>
            )}
            {hasStock && (
              <button className="view-stock-button">
                {matchedProductCount ? 
                  `View Stock (${matchedProductCount} matched)` : 
                  'View Stock'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  });

  // Update the renderLocationRow function to check for product matches
  function renderLocationRow(record, type) {
    const locationProducts = locationProductsMap.get(record.fields.LitAlertsId) || [];
    const hasStock = locationProducts.length > 0;
    
    // Check if this location has any products matching the current filter
    let matchedProductCount = 0;
    if (filteredProducts && hasStock) {
      const filteredProductIds = new Set(filteredProducts.data.map(p => p.id));
      matchedProductCount = locationProducts.filter(p => filteredProductIds.has(p.id)).length;
    }
    
    return (
      <LocationRow 
        key={record.id}
        record={record}
        type={type}
        hasStock={hasStock}
        products={locationProducts}
        matchedProductCount={matchedProductCount}
      />
    );
  }

  // Add scroll handler
  useEffect(() => {
    function handleScroll() {
      const container = document.getElementById('list-of-locations');
      if (!container) return;
      // Prevent loading more if we're already at the end
      if (displayCount >= locations.length) return;

      // Get container's bounding rectangle
      const rect = container.getBoundingClientRect();
      
      // More conservative check for bottom visibility
      const isBottomVisible = rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 50;

      // Debounce the scroll handler
      if (window.scrollTimeout) {
        window.clearTimeout(window.scrollTimeout);
      }

      window.scrollTimeout = setTimeout(() => {
        if (isBottomVisible) {
          setDisplayCount(prev => Math.min(prev + 10, locations.length));
        }
      }, 100);
    }

    window.addEventListener('scroll', handleScroll);
    // Delay initial check to ensure accurate container dimensions
    setTimeout(handleScroll, 500);
    return () => {
      if (window.scrollTimeout) {
        window.clearTimeout(window.scrollTimeout);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [locations.length, displayCount]);

  // Add the ProductFilter component
  function ProductFilter() {
    return (
      <div className={`product-filter ${isFilterVisible ? 'visible' : ''}`}>
        <div className="filter-toggle">
          <button 
            onClick={() => setIsFilterVisible(!isFilterVisible)}
            className="filter-toggle-button"
          >
            {isFilterVisible ? 'Hide Filters' : 'Find Products'} 
            <span className="toggle-icon">{isFilterVisible ? '▲' : '▼'}</span>
          </button>
        </div>
        
        {isFilterVisible && (
          <form onSubmit={handleFilterSubmit} className="filter-form">
            <div className="filter-row">
              <div className="filter-field">
                <label htmlFor="product-search">Product Name</label>
                <input
                  type="text"
                  id="product-search"
                  placeholder="Search for products..."
                  value={productNameFilter}
                  onChange={(e) => setProductNameFilter(e.target.value)}
                />
              </div>
              
              <div className="filter-field search-mode">
                <label htmlFor="search-mode">Search Mode</label>
                <select 
                  id="search-mode"
                  value={searchMode}
                  onChange={(e) => setSearchMode(e.target.value)}
                >
                  <option value="exclusive">All Words (AND)</option>
                  <option value="inclusive">Any Word (OR)</option>
                  <option value="exact">Exact Phrase</option>
                </select>
              </div>
            </div>
            
            <div className="filter-row">
              <div className="filter-field price-range">
                <label htmlFor="min-price">Min Price</label>
                <input
                  type="number"
                  id="min-price"
                  placeholder="Min $"
                  min="0"
                  step="0.01"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                />
              </div>
              
              <div className="filter-field price-range">
                <label htmlFor="max-price">Max Price</label>
                <input
                  type="number"
                  id="max-price"
                  placeholder="Max $"
                  min="0"
                  step="0.01"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                />
              </div>
            </div>
            
            <div className="filter-actions">
              <button type="submit" className="filter-submit" disabled={isFiltering}>
                {isFiltering ? 'Searching...' : 'Search Products'}
              </button>
              <button type="button" className="filter-clear" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
            
            {filteredProducts && (
              <div className="filter-results">
                Found {filteredProducts.count} matching products at {
                  locations.filter(record => {
                    const locationProducts = locationProductsMap.get(record.fields.LitAlertsId) || [];
                    if (locationProducts.length === 0) return false;
                    
                    const filteredProductIds = new Set(filteredProducts.data.map(p => p.id));
                    return locationProducts.some(p => filteredProductIds.has(p.id));
                  }).length
                } stores.
              </div>
            )}
          </form>
        )}
      </div>
    );
  }

  // Update PickupOptions to remove the ProductFilter component
  function PickupOptions() {
    return (
      <div className="store-location">
        <h3>Pickup</h3>
        {locations.slice(0, displayCount).map(record => 
          record.fields.PickupURL && renderLocationRow(record, 'pickup')
        )}
        {displayCount < locations.length && (
          <div className="loading-more">Loading more locations...</div>
        )}
      </div>
    );
  }

  // Add DeliveryOptions component definition
  function DeliveryOptions() {
    return (
      <div className="store-location">
        <h3>Delivery</h3>
        {locations.slice(0, displayCount).map(record => 
          record.fields.DeliveryURL && renderLocationRow(record, 'delivery')
        )}
        {displayCount < locations.length && (
          <div className="loading-more">Loading more locations...</div>
        )}
      </div>
    );
  }

  function LoadingLocations() {
    return (
      <div className="store-notice">
        <h3>Loading locations and products...</h3>
      </div>
    );
  }

  function NoOptions() {
    return (
      <div className="store-notice">
        <h3>Oops!</h3>
        <p>No stores available for {props.pickupOrDelivery}.</p>
        {props.pickupOrDelivery === 'delivery' ? (
          <p>We currently don't deliver to {props.shopperLocation?.city}. If you'd like us to deliver to your area, <a href="mailto:hello@getgoodfeels.com">email us</a>.</p>
        ) : (
          <p>We currently only serve Massachusetts. If you'd like us to bring Good Feels to your state, <a href="mailto:hello@getgoodfeels.com">email us</a>.</p>
        )}
      </div>
    );
  }

  return (
    <div id="list-of-locations">
      { (locations.length > 0 && !loadingProducts) ? (
        <>
          {'delivery' === props.pickupOrDelivery ? <DeliveryOptions /> : <PickupOptions />}
          <ProductModal 
            products={modalData?.products || []}
            isOpen={!!modalData}
            onClose={() => setModalData(null)}
            location={modalData?.location}
          />
        </>
      ) : (
        (loadingLocations || loadingProducts) ? <LoadingLocations /> : <NoOptions />
      )}
    </div>
  );
}
export default Locations;
